/**
 * Queue for websocket batch operations. Keeps up to concurrency items in flight.
 * First send: one request with a batch of up to concurrency items. On each success/error
 * (completeOne), one slot is freed and one new request with one item from the queue is sent
 * so in-flight stays full until the queue is empty. onDrain when queue and in-flight are both empty.
 *
 * @example
 * const queue = new WebSocketBatchQueue({
 *   concurrency: 10,
 *   onSend: (items) => ws.sendMessage(ACTION_DELETE, { route_ids: items.map(buildPayload) }),
 *   onDrain: () => { refresh(); endSelect(); }
 * });
 * queue.enqueueMany(domains);
 */
export class WebSocketBatchQueue {
  /**
   * @param {Object} options
   * @param {number} [options.concurrency=50] - Max items in flight at once
   * @param {function(items: *[]): void} [options.onSend] - Called with a batch of items (up to concurrency) to send in one request
   * @param {function(): void} [options.onDrain] - Called when queue and in-flight are both empty
   * @param {function(item: *): void} [options.onCompleteOne] - Called when an item is completed (removed from in-flight)
   */
  constructor(options = {}) {
    const { concurrency = 50, onSend, onDrain, onCompleteOne } = options;
    /**
     * @description Items waiting to be processed
     */
    this._queue = [];
    /**
     * @description Items currently being processed
     */
    this._inFlight = [];
    /**
     * @description Maximum number of items that can be processed concurrently
     */
    this._concurrency = Math.max(1, concurrency);
    /**
     * @description Callback to send an item
     */
    this.onSend = typeof onSend === 'function' ? onSend : () => {};
    /**
     * @description Callback to be called when the queue is empty and no items are in flight
     */
    this.onDrain = typeof onDrain === 'function' ? onDrain : () => {};
    /**
     * @description Callback when an item is completed (called with the removed item)
     */
    this.onCompleteOne = typeof onCompleteOne === 'function' ? onCompleteOne : () => {};
  }

  get concurrency() {
    return this._concurrency;
  }

  get pendingCount() {
    return this._queue.length;
  }

  get inFlightCount() {
    return this._inFlight.length;
  }

  /**
   * Read-only snapshot of in-flight items (for batch status updates when response lacks per-item info).
   */
  get inFlightItems() {
    return [...this._inFlight];
  }

  get isEmpty() {
    return this._queue.length === 0 && this._inFlight.length === 0;
  }

  /**
   * @param {*} item - Item to process (will be passed to onSend)
   */
  enqueue(item) {
    this._queue.push(item);
    this._processNext();
  }

  /**
   * @param {*[]} items - Items to process
   */
  enqueueMany(items) {
    if (!Array.isArray(items) || items.length === 0) return;
    this._queue.push(...items);
    this._processNext();
  }

  /**
   * Mark one in-flight item as complete (success or error). Frees a slot and processes the next item.
   * When queue and in-flight become empty, onDrain is called.
   *
   * @param {function(item: *): boolean} [matcher] - Optional. (item) => true for the item that completed.
   *        If omitted, completes the oldest in-flight item (FIFO).
   */
  completeOne(matcher) {
    let index = 0;
    if (typeof matcher === 'function') {
      index = this._inFlight.findIndex(matcher);
      if (index === -1) {
        console.warn('WebSocketBatchQueue: matcher did not find item, completing oldest');
        index = 0;
      }
    }
    const removed = this._inFlight.splice(index, 1);
    if (removed.length > 0) {
      this._processNext();
      this.onCompleteOne(removed[0]);
    }
    if (this.isEmpty) {
      this.onDrain();
    }
  }

  /**
   * Mark all in-flight items as complete. Use when the backend returns a single response for a whole batch.
   */
  completeAll() {
    const toComplete = [...this._inFlight];
    this._inFlight = [];
    toComplete.forEach((item) => this.onCompleteOne(item));
    this._processNext();
    if (this.isEmpty) {
      this.onDrain();
    }
  }

  /**
   * Clear pending items. In-flight items are not cancelled (they will still complete and trigger onDrain when done).
   */
  clearPending() {
    this._queue = [];
  }

  /**
   * Fill in-flight up to concurrency: initial batch (many items in one request), then one item per request on each completion.
   * @private
   */
  _processNext() {
    if (this._queue.length === 0) return;
    const slotsFree = this._concurrency - this._inFlight.length;
    if (slotsFree <= 0) return;
    const takeCount = this._inFlight.length === 0
      ? Math.min(this._concurrency, this._queue.length)
      : 1;
    const batch = [];
    for (let i = 0; i < takeCount && this._queue.length > 0; i++) {
      batch.push(this._queue.shift());
    }
    if (batch.length === 0) return;
    this._inFlight.push(...batch);
    this.onSend(batch);
  }
}
